import { useCallback } from 'react';
import { useApi } from '@shopify/ui-extensions-react/customer-account';
import { logger } from '../utils/logger';
import { TIMING_CONFIG } from '../config/constants';
import {
  UseModalHandlersProps,
  UseModalHandlersReturn,
} from '../types';

/**
 * Custom hook for managing modal interaction handlers
 * @param props - Configuration object with dependencies and handlers
 * @returns Modal interaction handlers
 */
export const useModalHandlers = ({
  finalOrderId,
  orderDetails,
  files,
  seatRequirements,
  onClose,
  handleCloseModal,
  handleUpload,
}: UseModalHandlersProps): UseModalHandlersReturn => {
  const api = useApi<'customer-account.order-status.block.render'>();
  const { ui } = api;

  const handleClose = useCallback(() => {
    handleCloseModal();
    onClose();
  }, [handleCloseModal, onClose]);

  const handleCancel = useCallback(() => {
    ui.overlay.close('upload-modal');
  }, [ui.overlay]);

  const handleUploadClick = useCallback(async () => {
    const idToUse = finalOrderId || orderDetails?.id;

    if (!idToUse) {
      logger.warn('No order ID available for upload');
      return;
    }

    logger.info('Submitting upload', {
      orderId: idToUse,
      fileCount: files.length,
      fileNames: files.map((f) => f.name),
      fileSizes: files.map((f) => `${(f.size / 1024 / 1024).toFixed(2)}MB`),
      totalSize: `${(files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)}MB`,
      seatRequirements: seatRequirements?.totalPhotosRequired,
      timestamp: new Date().toISOString(),
    });

    if (!seatRequirements) {
      logger.warn('No seat requirements available for upload');
      return;
    }

    await handleUpload(idToUse, seatRequirements, () => {
      setTimeout(() => {
        ui.overlay.close('upload-modal');
      }, TIMING_CONFIG.SUCCESS_AUTO_CLOSE_DELAY);
    });
  }, [
    handleUpload,
    finalOrderId,
    orderDetails?.id,
    ui.overlay,
    files,
    seatRequirements
  ]);

  return {
    handleClose,
    handleCancel,
    handleUploadClick,
  };
};